import React, { useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { UploadCloud, Loader2, FileText, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ImportDialog({ open, onOpenChange, projectId, onImported, detailCapture = "auto" }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [reflow, setReflow] = useState(true);

  const pick = (f) => {
    if (!f) return;
    const ok = /\.(txt|docx|pdf)$/i.test(f.name);
    if (!ok) { toast.error("Please choose a .txt, .docx or .pdf file"); return; }
    setFile(f);
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("reflow", reflow ? "true" : "false");
    fd.append("detail_capture", detailCapture);
    try {
      const { data } = await api.post(`/projects/${projectId}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const detail = detailCapture === "review"
        ? `${(data.suggestions || []).length} detail(s) to review`
        : `${data.entities_added} details detected`;
      const acts = data.acts_found ? ` · ${data.acts_found} act(s)${data.acts_created < data.acts_found ? ` (${data.acts_found - data.acts_created} matched existing)` : ""}` : "";
      toast.success(`Imported ${data.chapters_created} chapter(s)${acts} · ${detail}`);
      setFile(null);
      onOpenChange(false);
      onImported?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) setFile(null); } }}>
      <DialogContent className="bg-popover" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <UploadCloud className="w-5 h-5 text-primary" /> Import a manuscript
          </DialogTitle>
          <DialogDescription>
            Upload previous work — we'll auto-split it into chapters and detect vital details into your codex.
          </DialogDescription>
        </DialogHeader>

        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 cursor-pointer transition-colors
            ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
          <input type="file" accept=".txt,.docx,.pdf" className="hidden"
            data-testid="upload-manuscript-input" onChange={(e) => pick(e.target.files?.[0])} />
          {file ? (
            <>
              <CheckCircle2 className="w-10 h-10 text-primary" />
              <div className="flex items-center gap-2 font-medium"><FileText className="w-4 h-4" /> {file.name}</div>
              <p className="text-xs text-muted-foreground">Click to choose a different file</p>
            </>
          ) : (
            <>
              <UploadCloud className="w-10 h-10 text-muted-foreground" />
              <p className="font-medium">Drop your file here, or click to browse</p>
              <p className="text-xs text-muted-foreground">Supports .txt, .docx and .pdf</p>
            </>
          )}
        </label>

        <div className="flex items-start gap-3 rounded-lg border border-border p-3">
          <Switch checked={reflow} onCheckedChange={setReflow} data-testid="reflow-toggle" className="mt-0.5" />
          <div>
            <div className="text-sm font-medium">Smart paragraph reflow</div>
            <p className="text-xs text-muted-foreground">
              Fixes broken mid-sentence line breaks from PDFs/Word by merging wrapped lines into flowing paragraphs.
              Turn off to keep every original line break exactly as-is.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 rounded-lg p-3">
          <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          Chapters are detected from lines like "Chapter 1" or "Prologue". Acts are detected from lines like "Act II: The Heist" or "Part Three", and each chapter is placed under its act. AI extraction may take a few seconds.
        </div>

        <Button onClick={doImport} disabled={!file || busy} data-testid="run-import-button" className="w-full gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing & analysing…</> : <><UploadCloud className="w-4 h-4" /> Import manuscript</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
